import { TokenProvisioner } from '@journeyapps-platform/lib-journey-core';
import * as jwt from 'jsonwebtoken';
import { AccountsPortalClient } from '@journeyapps-platform/sdk-accounts';

export class Token {
  private token: string;
  private expiry: number;
  private provision?: () => Promise<string>;

  constructor(provision?: () => Promise<string>) {
    this.provision = provision;
    this.token = '';
    this.expiry = -1;
  }

  async getToken() {
    if (!this.valid()) {
      this.token = '';
      if (!this.provision) {
        throw new Error('Token provisioner is not yet configured');
      }
      const token = await this.provision();
      const claim = jwt.decode(token, { complete: true });
      // @ts-ignore
      this.expiry = claim.payload.exp * 1000;
      this.token = token;
    }

    return this.token;
  }

  /**
   * Sets the provider for cases where the provider can only be established
   * after the creation of the token handler.
   */
  setProvisioningProvider(provider: () => Promise<string>) {
    this.provision = provider;
  }

  valid() {
    return !!this.token && this.expiry > Date.now();
  }
}

export type AdminTokenProvisionerOptions = {
  accountsPortalClient: AccountsPortalClient;
};

export class AdminTokenProvisioner implements TokenProvisioner {
  client: AccountsPortalClient;
  hubToken: Token;
  sensitiveUserHubToken: Token;
  developerTokens: { [appId: string]: Token };

  constructor(protected options: AdminTokenProvisionerOptions) {
    this.client = options.accountsPortalClient;

    this.hubToken = new Token(async () => {
      return await this.client.issueAccountsHubToken();
    });

    this.sensitiveUserHubToken = new Token();

    this.developerTokens = {};
  }

  /**
   * Provision a JWT token for making API requests against the AccountsHub v4 API
   */
  async provisionHubToken() {
    return this.hubToken.getToken();
  }

  /**
   * This will prompt via the MFA store if an
   * existing token is invalid
   */
  async provisionSensitiveHubToken() {
    return this.sensitiveUserHubToken.getToken();
  }

  async provisionDeveloperToken(appId: string) {
    if (!this.developerTokens[appId]) {
      this.developerTokens[appId] = new Token(async () => {
        return await this.client.issueDeveloperToken({
          app_id: appId
        });
      });
    }
    return this.developerTokens[appId].getToken();
  }
}
